/**
 * CP-8.3 — Phase 8 Notifications surfaces.
 *
 * Exports:
 *  - NotifBell        Header bell icon + popover dropdown (unread badge,
 *                      most-recent 5, "View all" link).
 *  - NotifInbox       Dedicated /app/notifications page (filters, mark-read,
 *                      sticky-lock badge for r2 §9.1.4 unread_lock rows).
 *  - NotifPrefs       /app/notifications/preferences (per-category matrix
 *                      with locked-category disable + tooltip).
 *
 * r2 lock UI behaviors:
 *  - Sticky `unread_lock=true` rows show an amber "REVIEW REQUIRED" badge and
 *    the mark-read button is disabled (the server also rejects 409
 *    REVIEW_NOT_COMPLETE; UI is defense-in-depth).
 *  - Locked preference categories (`invoice-review-required`, `billing-admin`)
 *    render their toggles greyed-out with a tooltip explaining the platform
 *    policy lock.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, BellRing, Mail, Inbox, Lock, AlertCircle,
         CheckCheck, Filter, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { TID } from "@/lib/testIds";


const CATEGORY_LABELS = {
  rfq: "RFQs",
  bookings: "Bookings",
  crews: "Crews & invitations",
  timesheets: "Timesheets",
  completion: "Completion",
  ratings: "Ratings",
  estimates: "Estimates",
  billing: "Billing",
  "billing-admin": "Billing (admin)",
  payouts: "Payouts",
  "invoice-review-required": "Invoice review",
};


function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  try { return d.toLocaleDateString(); } catch { return iso; }
}


// ============================================================================
// Bell + dropdown (rendered inside Layout header)
// ============================================================================

export function NotifBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Poll unread count every 30s.
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const r = await api("/me/notifications/unread-count");
        if (!cancelled) setUnread(r.unread_count || 0);
      } catch { /* silent */ }
    }
    tick();
    const iv = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // When opened, fetch the most-recent 5 in-app notifications.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api("/me/notifications?limit=5")
      .then((r) => { if (!cancelled) setItems(r.data || []); })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (!e.target.closest?.("[data-notif-bell-root]")) setOpen(false);
    }
    if (open) document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]);

  return (
    <div className="relative" data-notif-bell-root>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={TID.notifBell}
        aria-label="Notifications"
        className="relative p-2 rounded-full text-[var(--avcp-ink-soft)] hover:text-[var(--avcp-ink)] hover:bg-slate-100 transition"
      >
        {unread > 0 ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        {unread > 0 && (
          <span
            data-testid={TID.notifBellBadge}
            className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid={TID.notifDropdown}
          className="absolute right-0 mt-2 w-80 sm:w-96 rounded-lg border border-slate-200 bg-white shadow-xl overflow-hidden z-50"
        >
          <div className="flex items-center justify-between border-b border-slate-100 p-4 bg-slate-50">
            <span className="font-semibold text-sm text-slate-900">Notifications</span>
            <span className="text-xs text-slate-500">{unread} unread</span>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {loading && (
              <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                You&apos;re all caught up.
              </div>
            )}
            {!loading && items.map((n) => (
              <DropdownItem key={n.id} n={n} />
            ))}
          </div>
          <Link
            to="/app/notifications"
            data-testid={TID.notifDropdownViewAll}
            onClick={() => setOpen(false)}
            className="block p-3 text-center text-sm font-medium text-[var(--avcp-primary)] border-t border-slate-100 bg-slate-50 hover:bg-slate-100 transition"
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );
}


function DropdownItem({ n }) {
  const isReviewLocked = n.unread_lock;
  // CP-14.6.3 — honor context.deep_link so RFQ / future links route directly
  // to their detail page instead of dumping into the generic inbox.
  const href = n.context?.deep_link || "/app/notifications";
  return (
    <Link
      to={href}
      data-testid={TID.notifDropdownItem}
      className={`flex items-start gap-3 p-4 border-b border-slate-50 hover:bg-slate-50 transition cursor-pointer ${
        n.read_at ? "" : "bg-[#06b6d4]/5"
      }`}
    >
      <div className="mt-0.5">
        {isReviewLocked ? (
          <Lock className="w-4 h-4 text-amber-600" />
        ) : (
          <Mail className="w-4 h-4 text-slate-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-slate-900 truncate">
            {n.subject || n.event_type}
          </span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {CATEGORY_LABELS[n.category] || n.category} · {fmtRel(n.created_at)}
        </div>
      </div>
    </Link>
  );
}


// ============================================================================
// Inbox page — /app/notifications
// ============================================================================

const ALL_CATEGORIES_USER = [
  "rfq",
  "bookings", "crews", "timesheets", "completion", "ratings",
  "estimates", "billing", "payouts",
];

export function NotifInbox() {
  return <Layout><NotifInboxInner /></Layout>;
}

function NotifInboxInner() {
  const [category, setCategory] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("limit", "100");
      if (category) qs.set("category", category);
      if (unreadOnly) qs.set("unread_only", "true");
      const r = await api(`/me/notifications?${qs.toString()}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [category, unreadOnly]);

  async function markRead(n) {
    try {
      await api(`/me/notifications/${n.id}/read`, { method: "POST" });
      refresh();
    } catch (e) {
      if (e.problem?.code === "REVIEW_NOT_COMPLETE") {
        toast.error("This notification stays sticky until the invoice is reviewed.");
      } else {
        toast.error(e.message || "Could not mark read");
      }
    }
  }

  async function markAll() {
    try {
      const r = await api("/me/notifications/read-all", { method: "POST" });
      toast.success(`Marked ${r.marked_read || 0} as read.`);
      refresh();
    } catch (e) {
      toast.error(e.message || "Could not mark all read");
    }
  }

  return (
    <div data-testid={TID.notifInboxRoot} className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Notifications
        </h1>
        <button
          type="button"
          onClick={markAll}
          data-testid={TID.notifMarkAllRead}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-sm text-slate-700 transition"
        >
          <CheckCheck className="w-4 h-4" /> Mark all read
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="col-span-1 lg:col-span-3 flex flex-col gap-3">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-2">
              <Filter className="w-3.5 h-3.5" /> Filters
            </div>
            <label className="block text-xs text-slate-600 mb-1">Category</label>
            <select
              data-testid={TID.notifInboxFilterCategory}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white"
            >
              <option value="">All categories</option>
              {ALL_CATEGORIES_USER.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                data-testid={TID.notifInboxUnreadOnly}
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
              />
              Unread only
            </label>
          </div>
        </aside>

        <main className="col-span-1 lg:col-span-9 flex flex-col gap-3">
          {loading && (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Loading notifications…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-12 text-center">
              <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <div className="text-sm text-slate-500">
                Nothing to show with the current filters.
              </div>
            </div>
          )}
          {!loading && items.map((n) => (
            <InboxRow key={n.id} n={n} onMarkRead={() => markRead(n)} />
          ))}
        </main>
      </div>
    </div>
  );
}


function InboxRow({ n, onMarkRead }) {
  const isUnread = !n.read_at && n.state !== "read";
  const isSticky = n.unread_lock;
  return (
    <div
      data-testid={TID.notifInboxRow}
      className={`rounded-lg border ${isUnread ? "border-l-4 border-l-[var(--avcp-primary)]" : ""} border-slate-200 bg-white p-5 shadow-sm`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isSticky && (
              <span
                data-testid={TID.notifStickyBadge}
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 px-2 py-0.5 text-[10px] font-semibold text-amber-700 uppercase tracking-wide"
              >
                <Lock className="w-3 h-3" /> Review required
              </span>
            )}
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
              {CATEGORY_LABELS[n.category] || n.category}
            </span>
            {isUnread && (
              <span className="inline-flex w-1.5 h-1.5 rounded-full bg-[var(--avcp-primary)]" />
            )}
          </div>
          <div className="font-medium text-slate-900">
            {n.subject || n.event_type}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {n.event_type} · {fmtRel(n.created_at)}
          </div>
        </div>
        {isUnread && (
          <button
            type="button"
            onClick={onMarkRead}
            disabled={isSticky}
            data-testid={TID.notifMarkRead}
            title={isSticky ? "Linked invoice still needs admin review" : "Mark as read"}
            className={`text-xs px-3 py-1.5 rounded-full transition ${
              isSticky
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-slate-100 hover:bg-slate-200 text-slate-700"
            }`}
          >
            Mark read
          </button>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// Preferences page — /app/notifications/preferences
// ============================================================================

export function NotifPrefs() {
  return <Layout><NotifPrefsInner /></Layout>;
}

function NotifPrefsInner() {
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await api("/me/notification-preferences");
      setPrefs(r.data || []);
    } catch (e) {
      toast.error(e.message || "Failed to load preferences");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  function toggle(cat, field) {
    setPrefs((cur) => cur.map((p) =>
      p.category === cat ? { ...p, [field]: !p[field] } : p
    ));
  }

  async function save() {
    setSaving(true);
    try {
      const updates = prefs
        .filter((p) => !p.locked)
        .map((p) => ({
          category: p.category, email: !!p.email,
          in_app: !!p.in_app, digest_only: !!p.digest_only,
        }));
      await api("/me/notification-preferences", {
        method: "PATCH", body: { updates },
      });
      toast.success("Preferences saved.");
      refresh();
    } catch (e) {
      toast.error(e.message || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid={TID.notifPrefsRoot} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Notification preferences
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Choose which categories reach you by email vs. in-app, and which
            should be rolled up into a digest.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={TID.notifPrefsSave}
          className="px-4 py-1.5 rounded-full bg-[var(--avcp-primary)] hover:opacity-90 text-white text-sm font-medium transition"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr>
              <th className="border-b border-slate-200 bg-slate-50 p-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Category</th>
              <th className="border-b border-slate-200 bg-slate-50 p-4 text-xs uppercase tracking-wider text-slate-500 font-semibold text-center">Email</th>
              <th className="border-b border-slate-200 bg-slate-50 p-4 text-xs uppercase tracking-wider text-slate-500 font-semibold text-center">In-app</th>
              <th className="border-b border-slate-200 bg-slate-50 p-4 text-xs uppercase tracking-wider text-slate-500 font-semibold text-center">Digest only</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="p-6 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && prefs.map((p) => (
              <PrefRow key={p.category} p={p} onToggle={toggle} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function PrefRow({ p, onToggle }) {
  const locked = p.locked;
  return (
    <tr data-testid={TID.notifPrefsRow}>
      <td className="border-b border-slate-100 p-4">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900">{CATEGORY_LABELS[p.category] || p.category}</span>
          {locked && (
            <span
              data-testid={TID.notifPrefsLockedBadge}
              title="This category is locked on by platform policy and cannot be disabled."
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600 uppercase tracking-wide"
            >
              <Lock className="w-3 h-3" /> Locked
            </span>
          )}
        </div>
      </td>
      <td className="border-b border-slate-100 p-4 text-center">
        <Toggle
          testid={TID.notifPrefsEmailToggle}
          checked={!!p.email} disabled={locked}
          onChange={() => onToggle(p.category, "email")}
        />
      </td>
      <td className="border-b border-slate-100 p-4 text-center">
        <Toggle
          testid={TID.notifPrefsInappToggle}
          checked={!!p.in_app} disabled={locked}
          onChange={() => onToggle(p.category, "in_app")}
        />
      </td>
      <td className="border-b border-slate-100 p-4 text-center">
        <Toggle
          testid={TID.notifPrefsDigestToggle}
          checked={!!p.digest_only} disabled={locked}
          onChange={() => onToggle(p.category, "digest_only")}
        />
      </td>
    </tr>
  );
}


function Toggle({ checked, disabled, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange()}
      data-testid={testid}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
        checked ? "bg-[var(--avcp-primary)]" : "bg-slate-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
